import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

const HomeServices = () => {
    const { t, i18n } = useTranslation();
    const { lang: urlLang } = useParams();
    const currentLang = urlLang || i18n.language || 'en';

    const services = [
        {
            id: 'web-dev',
            icon: '⚡',
            accent: '#6c5ce7',
            gradient: 'linear-gradient(135deg, rgba(108, 92, 231, 0.25), rgba(162, 155, 254, 0.05))',
            title: t('services.webDevelopment', 'პროფესიონალური ვებ დეველოპმენტი'),
            description: t('services.webDevelopmentContent', 'ვქმნით მაღალი სიჩქარის, SEO-ოპტიმიზირებულ და მომხმარებელზე ორიენტირებულ ვებსაიტებს React, PHP, და .NET ტექნოლოგიების გამოყენებით. ყოველი სტრუქტურა აგებულია სტანდარტების მიხედვით, რაც უზრუნველყოფს მაქსიმალურ სტაბილურობას.'),
            link: `/${currentLang}/services/web-development`,
            tags: ['React', 'PHP / .NET', 'REST APIs', 'Fast & SEO-Ready'],
            badge: 'Core Service'
        },
        {
            id: 'mobile-apps',
            icon: '📱',
            accent: '#0ea5e9',
            gradient: 'linear-gradient(135deg, rgba(14, 165, 233, 0.25), rgba(56, 189, 248, 0.05))',
            title: t('services.mobileApps', 'პერსონალური მობილური და ვებ აპლიკაციები'),
            description: t('services.mobileAppsContent', 'ვანალიზებთ თქვენს ბიზნეს მოდელს და ვქმნით უნიკალურ, მორგებულ აპლიკაციებს — როგორც ვებზე, ასევე მობილურ პლატფორმებზე. ჩვენი კოდი არის სუფთა, ტესტირებული და მზად მასშტაბირებისთვის.'),
            link: `/${currentLang}/services/mobile-apps`,
            tags: ['iOS & Android', 'Cross-Platform', 'Clean Architecture', 'Real-time Sync'],
            badge: 'Mobile & Web'
        },
        {
            id: 'infrastructure',
            icon: '🛡️',
            accent: '#10b981',
            gradient: 'linear-gradient(135deg, rgba(16, 185, 129, 0.25), rgba(52, 211, 153, 0.05))',
            title: t('services.infrastructure', 'უსაფრთხო და სტაბილური ინფრასტრუქტურა'),
            description: t('services.infrastructureContent', 'Docker, PostgreSQL, და თანამედროვე CI/CD პიპლაინების გამოყენებით ვუზრუნველყოფთ თქვენს სისტემებს მაქსიმალურ უსაფრთხოებას, სიმძლავრეს და uptime-ს. ჩვენი არქიტექტურა არ იშლება — იზრდება.'),
            link: `/${currentLang}/services/infrastructure`,
            tags: ['Docker / K8s', 'PostgreSQL', 'CI/CD Automation', '99.9% Uptime'],
            badge: 'DevOps & Security'
        },
        {
            id: 'partnership',
            icon: '🚀',
            accent: '#f59e0b',
            gradient: 'linear-gradient(135deg, rgba(245, 158, 11, 0.25), rgba(251, 191, 36, 0.05))',
            title: t('services.partnership', 'სრული ციფრული პარტნიორობა'),
            description: t('services.partnershipContent', 'Orbitronix არ არის მხოლოდ დეველოპმენტის სააგენტო — ჩვენ ვართ თქვენი ტექნოლოგიური პარტნიორი. ვუწევთ მხარს იდეიდან გაშვებამდე და მის შემდეგ: კონსულტაცია, დიზაინი, დეველოპმენტი, ტესტირება და მხარდაჭერა.'),
            link: `/${currentLang}/services/partnership`,
            tags: ['Dedicated Team', '24/7 SLA Support', 'Consulting & Audit', 'Long-term Scale'],
            badge: 'Full Partnership'
        }
    ];

    return (
        <section className="home-services-section" id="services">
            <div className="container">
                {/* Section Header */}
                <div className="home-services-head text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                    >
                        <span className="home-services-sub">
                            {t('services.title', 'ჩვენი სერვისები')}
                        </span>
                        <h2 className="home-services-title">
                            {t('services.subtitle', 'სტაბილური სერვისები. სწრაფი დეველოპმენტი. ელეგანტური კოდი.')}
                        </h2>
                        <p className="home-services-desc mx-auto">
                            {currentLang === 'ka'
                                ? 'Orbitronix გთავაზობთ სრულ ციკლურ ტექნოლოგიურ მომსახურებას: საწყისი ანალიზიდან და არქიტექტურიდან მასშტაბურ დანერგვამდე და 24/7 მხარდაჭერამდე.'
                                : 'Orbitronix delivers full-cycle engineering services: from architecture and high-performance development to cloud deployment and 24/7 technical support.'}
                        </p>
                    </motion.div>
                </div>

                {/* Service Cards Grid */}
                <div className="row g-4 mt-2">
                    {services.map((service, index) => (
                        <div className="col-lg-6 col-md-6" key={service.id}>
                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: '-50px' }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                                className="h-100"
                            >
                                <div 
                                    className="hs-card"
                                    style={{
                                        '--card-accent': service.accent,
                                        '--card-gradient': service.gradient
                                    }}
                                >
                                    {/* Top Icon & Badge */}
                                    <div className="hs-card-top">
                                        <div className="hs-icon-wrapper">
                                            <span className="hs-icon">{service.icon}</span>
                                        </div>
                                        <span className="hs-badge">{service.badge}</span>
                                    </div>

                                    {/* Content */}
                                    <h3 className="hs-card-title">{service.title}</h3>
                                    <p className="hs-card-desc">{service.description}</p>

                                    {/* Feature Pills */}
                                    <div className="hs-tags">
                                        {service.tags.map((tag, i) => (
                                            <span key={i} className="hs-tag">
                                                {tag}
                                            </span>
                                        ))}
                                    </div>

                                    {/* Footer Action */}
                                    <div className="hs-card-footer">
                                        <Link 
                                            to={service.link} 
                                            className="hs-btn"
                                            aria-label={`${t('common.learnMore', 'Learn More')} - ${service.title}`}
                                            title={`${t('common.learnMore', 'Learn More')} - ${service.title}`}
                                        >
                                            <span>{t('common.learnMore', 'გაიგეთ მეტი')}</span>
                                            <i className="fas fa-arrow-right" />
                                        </Link>
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    ))}
                </div>

                {/* View all services CTA */}
                <div className="text-center mt-5">
                    <Link to={`/${currentLang}/services`} className="hs-view-all">
                        {currentLang === 'ka' ? 'ყველა სერვისის დეტალურად ნახვა' : 'Explore All Services'}
                        <i className="fas fa-arrow-right ms-2" />
                    </Link>
                </div>
            </div>

            <style>{`
                .home-services-section {
                    padding: 100px 0;
                    position: relative;
                    background: radial-gradient(circle at 50% 20%, rgba(108, 92, 231, 0.06) 0%, transparent 70%);
                }
                .home-services-head {
                    margin-bottom: 50px;
                }
                .home-services-sub {
                    display: inline-block;
                    color: #6c5ce7;
                    font-size: 13px;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 3px;
                    margin-bottom: 12px;
                }
                .home-services-title {
                    color: #ffffff;
                    font-size: clamp(1.8rem, 3.4vw, 2.7rem);
                    font-weight: 800;
                    line-height: 1.25;
                    margin-bottom: 16px;
                }
                .home-services-desc {
                    color: rgba(255, 255, 255, 0.6);
                    font-size: 15px;
                    line-height: 1.7;
                    max-width: 680px;
                }
                .hs-card {
                    position: relative;
                    height: 100%;
                    display: flex;
                    flex-direction: column;
                    background: rgba(14, 19, 44, 0.7);
                    backdrop-filter: blur(12px);
                    border: 1px solid rgba(255, 255, 255, 0.08);
                    border-radius: 24px;
                    padding: 34px 30px;
                    transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
                    overflow: hidden;
                }
                .hs-card::before {
                    content: '';
                    position: absolute;
                    inset: 0;
                    background: var(--card-gradient);
                    opacity: 0;
                    transition: opacity 0.4s ease;
                    pointer-events: none;
                }
                .hs-card::after {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    height: 3px;
                    background: var(--card-accent);
                    opacity: 0;
                    transition: opacity 0.4s ease;
                }
                .hs-card:hover {
                    transform: translateY(-8px);
                    border-color: rgba(255, 255, 255, 0.2);
                    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4), 0 0 30px rgba(108, 92, 231, 0.12);
                }
                .hs-card:hover::before {
                    opacity: 1;
                }
                .hs-card:hover::after {
                    opacity: 1;
                }
                .hs-card-top {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    margin-bottom: 24px;
                    position: relative;
                    z-index: 1;
                }
                .hs-icon-wrapper {
                    width: 58px;
                    height: 58px;
                    border-radius: 16px;
                    background: rgba(255, 255, 255, 0.05);
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 28px;
                    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
                    transition: all 0.3s;
                }
                .hs-card:hover .hs-icon-wrapper {
                    transform: scale(1.1) rotate(4deg);
                    background: rgba(255, 255, 255, 0.1);
                    border-color: var(--card-accent);
                }
                .hs-badge {
                    font-size: 11px;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 1px;
                    padding: 4px 12px;
                    border-radius: 30px;
                    background: rgba(255, 255, 255, 0.06);
                    color: rgba(255, 255, 255, 0.7);
                    border: 1px solid rgba(255, 255, 255, 0.1);
                }
                .hs-card-title {
                    color: #ffffff;
                    font-size: 1.35rem;
                    font-weight: 700;
                    line-height: 1.35;
                    margin-bottom: 14px;
                    position: relative;
                    z-index: 1;
                }
                .hs-card-desc {
                    color: rgba(255, 255, 255, 0.65);
                    font-size: 14px;
                    line-height: 1.7;
                    margin-bottom: 24px;
                    flex-grow: 1;
                    position: relative;
                    z-index: 1;
                }
                .hs-tags {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 8px;
                    margin-bottom: 28px;
                    position: relative;
                    z-index: 1;
                }
                .hs-tag {
                    font-size: 12px;
                    font-weight: 600;
                    padding: 4px 10px;
                    border-radius: 8px;
                    background: rgba(255, 255, 255, 0.04);
                    color: rgba(255, 255, 255, 0.8);
                    border: 1px solid rgba(255, 255, 255, 0.08);
                }
                .hs-card-footer {
                    position: relative;
                    z-index: 1;
                    padding-top: 18px;
                    border-top: 1px solid rgba(255, 255, 255, 0.06);
                }
                .hs-btn {
                    display: inline-flex;
                    align-items: center;
                    gap: 10px;
                    color: var(--card-accent);
                    font-weight: 700;
                    font-size: 14px;
                    text-decoration: none;
                    transition: all 0.3s;
                }
                .hs-btn i {
                    transition: transform 0.3s;
                }
                .hs-card:hover .hs-btn {
                    color: #ffffff;
                }
                .hs-card:hover .hs-btn i {
                    transform: translateX(6px);
                }
                .hs-view-all {
                    display: inline-flex;
                    align-items: center;
                    color: #ffffff;
                    font-weight: 700;
                    text-decoration: none;
                    background: rgba(108, 92, 231, 0.15);
                    border: 1px solid rgba(108, 92, 231, 0.4);
                    padding: 14px 34px;
                    border-radius: 50px;
                    transition: all 0.3s;
                }
                .hs-view-all:hover {
                    background: #6c5ce7;
                    border-color: #6c5ce7;
                    color: #ffffff;
                    transform: translateY(-2px);
                    box-shadow: 0 10px 25px rgba(108, 92, 231, 0.4);
                }
            `}</style>
        </section>
    );
};

export default HomeServices;
